import { ref } from 'vue';
import { useApi } from './useApi';
import { useAppState } from './useAppState';
import { getHttpInstance, getApiConfig } from './useApi';
// Set true by pages that add/update/delete a mortgage so the next mortgage-info
// read (e.g. MoneyPage) knows to SWR+poll until the cached
// /CreditAccounts/Mortgages payload reflects the write.
export const pendingMortgageRevalidate = ref(false);
// Dedupe in-flight pollUntilChanged cycles so concurrent callers attach to the
// existing poll instead of each starting their own loop.
let mortgageInfoPolling = false;
const mortgageInfoWaiters = [];
// Stable change key for the mortgage-info SWR poll, used only as a fallback
// when the server hash is unavailable. Built from just the sorted account ids
// so adding/removing a mortgage flips the key, while volatile fields (balances)
// never bail the poll out early.
export function mortgageInfoChangeKey(info) {
    return (Array.isArray(info) ? info : []).map((a) => a.id).sort().join(',');
}
export function useMortgage() {
    const api = useApi();
    const state = useAppState();
    function getMortgageInfo(onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false) {
        const url = "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/Mortgages";
        // A pollUntilChanged cycle is already running — attach this caller's
        // callback to it rather than starting a second poll loop. Fire the stale
        // emission synchronously from state.mortgageInfo so the new caller gets
        // the cached payload immediately (matches the SWR contract).
        if (pollUntilChanged && mortgageInfoPolling) {
            if (onSuccess) {
                if (state.mortgageInfo)
                    onSuccess(/* isStale */ true);
                mortgageInfoWaiters.push(onSuccess);
            }
            return;
        }
        if (pollUntilChanged)
            mortgageInfoPolling = true;
        api.GET(url, (result, isStale) => {
            // A member with no mortgage doesn't get an empty list back, and every caller maps over this
            state.mortgageInfo = Array.isArray(result) ? result : [];
            if (onSuccess)
                onSuccess(isStale);
            if (!isStale && pollUntilChanged) {
                mortgageInfoPolling = false;
                const waiters = mortgageInfoWaiters.splice(0);
                waiters.forEach(cb => cb(/* isStale */ false));
            }
        }, !refreshCache, refreshCache, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged, mortgageInfoChangeKey);
    }
    function getMortgage(policyId, onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false, pollIntervalMs) {
        api.GET("/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/Mortgages/" + policyId, (result, isStale) => {
            state.policy = result;
            if (onSuccess)
                onSuccess(result, isStale);
        }, !refreshCache, refreshCache, undefined, false, staleWhileRevalidate, pollUntilChanged, undefined, pollIntervalMs);
    }
    function addMortgageAccount(mortgage, onSuccess = null) {
        getHttpInstance().post(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/Mortgages", mortgage)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => addMortgageAccount(mortgage, onSuccess));
            }
        });
    }
    function updateMortgageAccount(mortgage, onSuccess = null) {
        getHttpInstance().put(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/Mortgages/" + mortgage.id, mortgage)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => updateMortgageAccount(mortgage, onSuccess));
            }
        });
    }
    function deleteMortgageAccount(id, onSuccess = null) {
        getHttpInstance().delete(getApiConfig().api + "/Customers/00000000-0000-0000-0000-000000000000/CreditAccounts/Mortgages/" + id)
            .then((response) => { if (onSuccess)
            onSuccess(response.data); })
            .catch((result) => {
            if (result.response && result.response.status == 401) {
                api.refreshLogin(() => deleteMortgageAccount(id, onSuccess));
            }
        });
    }
    function getInterestRateType(interestRateType) {
        if (interestRateType == null)
            return null;
        if (interestRateType == 0)
            return 'Fixed';
        if (interestRateType == 1)
            return 'Standard Variable';
        if (interestRateType == 2)
            return 'Discounted Tracker';
        return 'Unknown';
    }
    function calculateMonthsRemaining(endDate) {
        const now = new Date();
        const end = new Date(endDate);
        const years = end.getFullYear() - now.getFullYear();
        const months = end.getMonth() - now.getMonth();
        return years * 12 + months;
    }
    function calculateDateProgressPercentage(start, end) {
        const now = new Date();
        const startDate = new Date(start);
        const endDate = new Date(end);
        const total = endDate.getTime() - startDate.getTime();
        const elapsed = now.getTime() - startDate.getTime();
        return Math.round((elapsed / total) * 100);
    }
    return {
        getMortgageInfo,
        getMortgage,
        addMortgageAccount,
        updateMortgageAccount,
        deleteMortgageAccount,
        getInterestRateType,
        calculateMonthsRemaining,
        calculateDateProgressPercentage,
    };
}
